export type StrapiEntity<TAttributes> = {
  id: number;
  attributes: TAttributes;
};

export type StrapiPagination = {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
};

export type StrapiMeta = {
  pagination?: StrapiPagination;
};

export type StrapiCollectionResponse<TAttributes> = {
  data: Array<StrapiEntity<TAttributes>>;
  meta: StrapiMeta;
};

export type StrapiSingleResponse<TAttributes> = {
  data: StrapiEntity<TAttributes> | null;
  meta: StrapiMeta;
};

export type StrapiErrorBody = {
  error: {
    status: number;
    name: string;
    message: string;
    details?: unknown;
  };
};

export type StrapiRichTextNode = {
  type: "text";
  text: string;
};

export type StrapiRichTextBlock = {
  type: "paragraph" | string;
  children: StrapiRichTextNode[];
};

export class FetchApiError extends Error {
  readonly status?: number;
  readonly details?: unknown;

  constructor(message: string, options?: { status?: number; details?: unknown; cause?: unknown }) {
    super(message, options?.cause ? { cause: options.cause } : undefined);
    this.name = "FetchApiError";
    this.status = options?.status;
    this.details = options?.details;
  }
}

type NextFetchConfig = {
  revalidate?: number | false;
  tags?: string[];
};

type StrapiFetchInit = RequestInit & { next?: NextFetchConfig };

type QueryPrimitive = string | number | boolean | null;
export type StrapiQueryValue = QueryPrimitive | StrapiQuery | StrapiQueryValue[];
export interface StrapiQuery {
  [key: string]: StrapiQueryValue | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function extractStrapiMediaUrl(value: unknown): string | null {
  const normalizeUrl = (url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    if (!url.startsWith("/")) return url;
    const baseUrl = process.env.NEXT_PUBLIC_STRAPI_BASE_URL;
    if (!baseUrl) return url;
    const trimmed = baseUrl.replace(/\/+$/, "");
    const mediaBase = trimmed.endsWith("/api") ? trimmed.slice(0, -4) : trimmed;
    return `${mediaBase}${url}`;
  };

  if (!value) return null;
  if (typeof value === "string") return normalizeUrl(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const url = extractStrapiMediaUrl(item);
      if (url) return url;
    }
    return null;
  }

  if (!isRecord(value)) return null;

  const candidates: Array<{ key: string; url: string; width?: number }> = [];

  const formats = value.formats;
  if (isRecord(formats)) {
    for (const [key, format] of Object.entries(formats)) {
      if (!isRecord(format)) continue;
      const url = format.url;
      if (typeof url !== "string" || !url) continue;
      const width = typeof format.width === "number" ? format.width : undefined;
      candidates.push({ key, url: normalizeUrl(url), width });
    }
  }

  const directUrl = value.url;
  if (typeof directUrl === "string" && directUrl) {
    const width = typeof value.width === "number" ? value.width : undefined;
    candidates.push({ key: "original", url: normalizeUrl(directUrl), width });
  }

  const withWidth = candidates.filter((item) => typeof item.width === "number") as Array<
    { key: string; url: string; width: number }
  >;
  if (withWidth.length) {
    withWidth.sort((a, b) => b.width - a.width);
    return withWidth[0].url;
  }

  const priority = ["original", "large", "medium", "small", "thumbnail"];
  for (const key of priority) {
    const found = candidates.find((item) => item.key === key);
    if (found) return found.url;
  }

  if (candidates.length) return candidates[0].url;

  const data = value.data;
  if (data) return extractStrapiMediaUrl(data);

  const attributes = value.attributes;
  if (attributes) return extractStrapiMediaUrl(attributes);

  return null;
}

export function extractTextFromDescription(description: string | StrapiRichTextBlock[] | null | undefined): string {
  if (typeof description === "string") {
    return description;
  }

  if (!Array.isArray(description)) {
    return "";
  }

  return description
    .map((block) => {
      if (block.type === "paragraph" && Array.isArray(block.children)) {
        return block.children
          .map((child) => {
            if (child.type === "text" && typeof child.text === "string") {
              return child.text;
            }
            return "";
          })
          .join("");
      }
      return "";
    })
    .join(" ");
}

function encodeKeyPart(part: string): string {
  return encodeURIComponent(part);
}

function encodeValue(value: QueryPrimitive): string {
  return value === null ? "null" : encodeURIComponent(String(value));
}

function buildPairs(key: string, value: StrapiQueryValue): string[] {
  if (Array.isArray(value)) {
    const pairs: string[] = [];
    value.forEach((item, index) => {
      const nextKey = `${key}[${index}]`;
      pairs.push(...buildPairs(nextKey, item));
    });
    return pairs;
  }
  if (isRecord(value)) {
    const keys = Object.keys(value).sort();
    const pairs: string[] = [];
    for (const k of keys) {
      const nextKey = `${key}[${encodeKeyPart(k)}]`;
      const nextValue = value[k] as StrapiQueryValue | undefined;
      if (nextValue === undefined) continue;
      pairs.push(...buildPairs(nextKey, nextValue));
    }
    return pairs;
  }

  return [`${key}=${encodeValue(value)}`];
}

export function toStrapiQueryString(query?: StrapiQuery): string {
  if (!query) return "";
  const keys = Object.keys(query).sort();
  const pairs: string[] = [];
  for (const key of keys) {
    const encodedKey = encodeKeyPart(key);
    const value = query[key];
    if (value === undefined) continue;
    pairs.push(...buildPairs(encodedKey, value));
  }
  return pairs.join("&");
}

export function getStrapiBaseUrl(): string {
  const baseUrl = process.env.NEXT_PUBLIC_STRAPI_BASE_URL;
  if (!baseUrl) {
    throw new FetchApiError("Missing NEXT_PUBLIC_STRAPI_BASE_URL environment variable");
  }
  return baseUrl.replace(/\/+$/, "");
}

export function getBrandSlug(): string {
  const slug = process.env.NEXT_PUBLIC_BRAND_SLUG;
  if (!slug) throw new FetchApiError("Missing NEXT_PUBLIC_BRAND_SLUG environment variable");
  return slug;
}

export function buildStrapiUrl(path: string, query?: StrapiQuery): string {
  if (/^https?:\/\//i.test(path)) return path;

  const baseUrl = getStrapiBaseUrl();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const queryString = toStrapiQueryString(query);
  return queryString ? `${baseUrl}${normalizedPath}?${queryString}` : `${baseUrl}${normalizedPath}`;
}

export function buildStrapiUrlFromQueryString(path: string, queryString?: string): string {
  const normalizedQueryString = queryString ? queryString.replace(/^\?/, "") : "";
  if (/^https?:\/\//i.test(path)) {
    if (!normalizedQueryString) return path;
    return path.includes("?") ? `${path}&${normalizedQueryString}` : `${path}?${normalizedQueryString}`;
  }

  const baseUrl = getStrapiBaseUrl();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return normalizedQueryString
    ? `${baseUrl}${normalizedPath}?${normalizedQueryString}`
    : `${baseUrl}${normalizedPath}`;
}

function mergeBrandFilterQuery(
  query: StrapiQuery | undefined,
  options: { brandSlug: string; brandRelationKey?: string },
): StrapiQuery {
  const brandRelationKey = options.brandRelationKey ?? "brand";
  const next: StrapiQuery = query ? { ...query } : {};

  const filters = isRecord(next.filters) ? { ...(next.filters as StrapiQuery) } : {};
  const brandFilter = isRecord(filters[brandRelationKey])
    ? { ...(filters[brandRelationKey] as StrapiQuery) }
    : {};

  brandFilter.slug = { $eq: options.brandSlug };
  filters[brandRelationKey] = brandFilter;
  next.filters = filters;

  return next;
}

function appendQueryStringPair(queryString: string, pair: string): string {
  const normalized = queryString.replace(/^\?/, "");
  if (!normalized) return pair;
  return `${normalized}&${pair}`;
}

function buildBrandFilterPair(options: { brandSlug: string; brandRelationKey?: string }): string {
  const relationKey = options.brandRelationKey ?? "brand";
  const key = `filters[${encodeKeyPart(relationKey)}][slug][${encodeKeyPart("$eq")}]`;
  return `${key}=${encodeValue(options.brandSlug)}`;
}

function normalizeErrorBody(json: unknown): StrapiErrorBody | null {
  if (!isRecord(json)) return null;
  const error = json.error;
  if (!isRecord(error)) return null;
  const status = error.status;
  const name = error.name;
  const message = error.message;
  if (typeof status !== "number" || typeof name !== "string" || typeof message !== "string") return null;
  return { error: { status, name, message, details: (error as { details?: unknown }).details } };
}

function normalizeStrapiEntityPayload(entity: unknown): unknown {
  if (!isRecord(entity)) return entity;
  const id = entity.id;
  if (typeof id !== "number") return entity;
  if ("attributes" in entity) return entity;
  const attributes = { ...(entity as Record<string, unknown>) };
  delete attributes.id;
  return { id, attributes };
}

function normalizeStrapiResponsePayload(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;
  if (!("data" in payload)) return payload;
  const data = (payload as { data?: unknown }).data;
  if (Array.isArray(data)) {
    return { ...payload, data: data.map(normalizeStrapiEntityPayload) };
  }
  return { ...payload, data: normalizeStrapiEntityPayload(data) };
}

export async function fetchStrapi<TResponse>(
  path: string,
  options?: {
    query?: StrapiQuery;
    queryString?: string;
    init?: StrapiFetchInit;
    withBrandFilter?: boolean;
    brandSlug?: string;
    brandRelationKey?: string;
  },
): Promise<TResponse> {
  const {
    query,
    queryString,
    init,
    withBrandFilter = true,
    brandSlug = getBrandSlug(),
    brandRelationKey,
  } = options ?? {};

  const finalQueryString = (() => {
    if (queryString !== undefined) {
      return withBrandFilter && brandSlug
        ? appendQueryStringPair(queryString, buildBrandFilterPair({ brandSlug, brandRelationKey }))
        : queryString;
    }

    const mergedQuery =
      withBrandFilter && brandSlug
        ? mergeBrandFilterQuery(query, { brandSlug, brandRelationKey })
        : (query ?? {});

    return toStrapiQueryString(mergedQuery);
  })();

  const url = buildStrapiUrlFromQueryString(path, finalQueryString);
  const response = await fetch(url, { ...init, method: init?.method ?? "GET" });

  let json: unknown = null;
  try {
    json = await response.json();
  } catch (error) {
    if (response.ok) throw new FetchApiError("Failed to parse Strapi response JSON", { cause: error });
  }

  const errorBody = normalizeErrorBody(json);
  if (errorBody) {
    throw new FetchApiError(errorBody.error.message, {
      status: errorBody.error.status,
      details: errorBody.error.details,
    });
  }

  if (!response.ok) {
    throw new FetchApiError(`Request failed: ${response.status} ${response.statusText}`, {
      status: response.status,
      details: json,
    });
  }

  return normalizeStrapiResponsePayload(json) as TResponse;
}
